import bboxPolygon from '@turf/bbox-polygon';
import turfCircle from '@turf/circle';
import { polygon } from '@turf/helpers';

function closeRing(ring) {
  if (ring.length === 0) return ring;
  const first = ring[0];
  const last = ring[ring.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    return [...ring, [...first]];
  }
  return ring;
}

/**
 * @param {google.maps.Polygon | google.maps.Circle | google.maps.Rectangle} overlay
 * @returns {import('@turf/helpers').Feature}
 */
export function overlayToFeature(overlay) {
  const g = window.google.maps;

  if (overlay instanceof g.Polygon) {
    const rawPaths = overlay.getPaths();
    const rings = [];
    for (let i = 0; i < rawPaths.getLength(); i += 1) {
      const ring = rawPaths
        .getAt(i)
        .getArray()
        .map(ll => [ll.lng(), ll.lat()]);
      rings.push(closeRing(ring));
    }
    if (rings.length === 0) {
      throw new Error('Empty polygon path');
    }
    return polygon(rings);
  }

  if (overlay instanceof g.Circle) {
    const center = overlay.getCenter();
    return turfCircle([center.lng(), center.lat()], overlay.getRadius(), {
      steps: 64,
      units: 'meters',
    });
  }

  if (overlay instanceof g.Rectangle) {
    const b = overlay.getBounds();
    const sw = b.getSouthWest();
    const ne = b.getNorthEast();
    return bboxPolygon([sw.lng(), sw.lat(), ne.lng(), ne.lat()]);
  }

  throw new Error('Unsupported overlay type');
}

/**
 * GeoJSON Polygon (axis-aligned rectangle) for a Google LatLngBounds / viewport.
 * @param {google.maps.LatLngBounds} bounds
 * @returns {import('@turf/helpers').Feature<import('@turf/helpers').Polygon>|null}
 */
export function polygonFeatureFromLatLngBounds(bounds) {
  if (!bounds || !window.google?.maps) return null;
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  if (!ne || !sw) return null;
  const neLat = typeof ne.lat === 'function' ? ne.lat() : ne.lat;
  const neLng = typeof ne.lng === 'function' ? ne.lng() : ne.lng;
  const swLat = typeof sw.lat === 'function' ? sw.lat() : sw.lat;
  const swLng = typeof sw.lng === 'function' ? sw.lng() : sw.lng;
  return bboxPolygon([swLng, swLat, neLng, neLat]);
}

/**
 * @param {import('@turf/helpers').Feature} feature
 * @param {typeof window.google.maps} g
 * @returns {google.maps.LatLngBounds|null}
 */
export function latLngBoundsFromFeature(feature, g) {
  const geom = feature?.geometry;
  if (!geom) return null;
  const b = new g.LatLngBounds();
  const extendRing = ring => {
    ring.forEach(([lng, lat]) => b.extend({ lat, lng }));
  };
  if (geom.type === 'Polygon') {
    geom.coordinates.forEach(extendRing);
  } else if (geom.type === 'MultiPolygon') {
    geom.coordinates.forEach(poly => poly.forEach(extendRing));
  } else {
    return null;
  }
  if (typeof b.isEmpty === 'function' && b.isEmpty()) return null;
  return b;
}

/**
 * @param {google.maps.Map} map
 * @param {import('@turf/helpers').Feature} feature
 * @param {Record<string, unknown>} pathOptions
 * @returns {google.maps.Polygon[]}
 */
export function featureToGooglePolygons(map, feature, pathOptions) {
  const g = window.google.maps;
  const geom = feature?.geometry;
  if (!geom) return [];

  const mk = paths =>
    new g.Polygon({
      map,
      paths,
      editable: false,
      draggable: false,
      ...pathOptions,
    });

  if (geom.type === 'Polygon') {
    const pathList = geom.coordinates.map(ring =>
      ring.map(([lng, lat]) => ({ lat, lng })),
    );
    return [mk(pathList)];
  }

  if (geom.type === 'MultiPolygon') {
    return geom.coordinates.map(polyCoords => {
      const pathList = polyCoords.map(ring =>
        ring.map(([lng, lat]) => ({ lat, lng })),
      );
      return mk(pathList);
    });
  }

  return [];
}

export const CIRCLE_EDIT_VERTEX_COUNT = 16;

/**
 * Replace a drawn circle with an editable polygon (16 vertices on the circumference).
 * @param {google.maps.Map} map
 * @param {google.maps.Circle} circle
 * @param {Record<string, unknown>} pathOptions stroke/fill etc.
 * @returns {google.maps.Polygon}
 */
export function createEditablePolygonFromCircle(map, circle, pathOptions) {
  const g = window.google.maps;
  const spherical = g.geometry?.spherical;
  if (!spherical) {
    throw new Error('Maps geometry library is required for circle editing');
  }
  const center = circle.getCenter();
  const radiusMeters = circle.getRadius();
  const path = [];
  for (let i = 0; i < CIRCLE_EDIT_VERTEX_COUNT; i += 1) {
    const heading = (360 / CIRCLE_EDIT_VERTEX_COUNT) * i;
    path.push(spherical.computeOffset(center, radiusMeters, heading));
  }
  return new g.Polygon({
    map,
    paths: path,
    editable: true,
    draggable: true,
    ...pathOptions,
  });
}

/**
 * Replace a drawn rectangle with an editable 4-vertex polygon.
 * @param {google.maps.Map} map
 * @param {google.maps.Rectangle} rect
 * @param {Record<string, unknown>} pathOptions
 * @returns {google.maps.Polygon}
 */
export function createEditablePolygonFromRectangle(map, rect, pathOptions) {
  const g = window.google.maps;
  const b = rect.getBounds();
  const ne = b.getNorthEast();
  const sw = b.getSouthWest();
  const nw = new g.LatLng(ne.lat(), sw.lng());
  const se = new g.LatLng(sw.lat(), ne.lng());
  const path = [nw, ne, se, sw];
  return new g.Polygon({
    map,
    paths: path,
    editable: true,
    draggable: true,
    ...pathOptions,
  });
}

/**
 * @param {google.maps.Polygon} poly
 * @param {Record<string, unknown>} pathOptions merged into setOptions
 */
export function enablePolygonVertices(poly, pathOptions) {
  poly.setOptions({
    editable: true,
    draggable: true,
    ...pathOptions,
  });
}

/**
 * Fires when vertices move, insert, remove, or whole polygon is dragged.
 * @param {google.maps.Polygon} poly
 * @param {() => void} onGeometryChange
 */
export function attachPolygonGeometryListeners(poly, onGeometryChange) {
  const g = window.google.maps;
  const bindPath = path => {
    ['insert_at', 'remove_at', 'set_at'].forEach(evt => {
      g.event.addListener(path, evt, onGeometryChange);
    });
  };
  const paths = poly.getPaths();
  for (let i = 0; i < paths.getLength(); i += 1) {
    bindPath(paths.getAt(i));
  }
  g.event.addListener(poly, 'dragend', onGeometryChange);
}
